import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  deleteDoc,
  doc,
  onSnapshot,
  runTransaction,
} from "firebase/firestore";
import { Button, Modal, Card } from "./UI";
import {
  ShoppingCartIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  CreditCardIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";

const UserOrderView = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [returnedOrderMessage, setReturnedOrderMessage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [errorMessage, setErrorMessage] = useState("");

  const [paymentSettings, setPaymentSettings] = useState({
    enabled: false,
    accountNumber: "",
    viber: "",
    qrCodeUrl: "",
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchPaymentSettings();

    // Set up real-time listener for returned orders
    const ordersQuery = query(
      collection(db, "orders"),
      where("status", "==", "returned")
    );

    const unsubscribeOrders = onSnapshot(
      ordersQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const returnedOrder = snapshot.docs[0];
          const orderData = returnedOrder.data();

          const cartItems = orderData.items.map((item) => ({
            ...item,
            quantity: item.quantity,
          }));
          setCart(cartItems);

          setReturnedOrderMessage(
            "Your order was returned by the cashier for modification. Please review and resubmit."
          );

          deleteDoc(doc(db, "orders", returnedOrder.id)).catch((error) =>
            console.error("Error deleting returned order:", error)
          );

          setTimeout(() => {
            setReturnedOrderMessage("");
          }, 10000);
        }
      },
      (error) => {
        console.error("Error listening for returned orders:", error);
      }
    );

    const paymentSettingsDoc = doc(db, "settings", "payment");
    const unsubscribeSettings = onSnapshot(
      paymentSettingsDoc,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log("Payment settings loaded:", data);
          setPaymentSettings({
            id: docSnapshot.id,
            ...data,
          });
        } else {
          console.log("No payment settings found");
        }
      }
    );

    const categoriesCollection = collection(db, "categories");
    const unsubscribeCategories = onSnapshot(
      categoriesCollection,
      (snapshot) => {
        const categoriesList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          order: doc.data().order || 0,
        }));

        const sortedCategories = categoriesList.sort(
          (a, b) => a.order - b.order
        );
        const categoryNames = sortedCategories.map((cat) => cat.name);
        setCategories(["All", ...categoryNames]);
      }
    );

    // Real-time listener for payment modal trigger from cashier
    const unsubscribePaymentModal = onSnapshot(
      doc(db, "ui_state", "payment_modal"),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log("Payment modal trigger received:", data);
          if (data.show) {
            console.log("Opening payment modal");
            console.log("Payment settings:", paymentSettings);
            console.log("Account Number:", paymentSettings.accountNumber);
            console.log("Viber:", paymentSettings.viber);
            console.log("QR Code URL:", paymentSettings.qrCodeUrl);

            // Play notification sound
            try {
              const audioContext = new (window.AudioContext ||
                window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              oscillator.frequency.value = 800;
              oscillator.type = "sine";

              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(
                0.01,
                audioContext.currentTime + 0.5
              );

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.5);
            } catch (err) {
              console.error("Error playing sound:", err);
            }

            // Show modal
            setShowPaymentModal(true);
            console.log("Modal state set to true");
          }
        }
      },
      (error) => {
        // Document doesn't exist yet, that's okay
        console.log("Payment modal state not yet initialized:", error);
      }
    );

    return () => {
      unsubscribeOrders();
      unsubscribeSettings();
      unsubscribeCategories();
      unsubscribePaymentModal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const productsCollection = collection(db, "products");
      const productsSnapshot = await getDocs(productsCollection);
      const productsList = productsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const settingsRef = collection(db, "settings");
      const settingsSnapshot = await getDocs(settingsRef);
      const settings = settingsSnapshot.docs.find(
        (doc) => doc.id === "payment"
      );
      if (settings) {
        setPaymentSettings(settings.data());
      }
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    }
  };

  // Removed auto-open modal - now showing card by default instead

  const handleClosePaymentModal = async () => {
    setShowPaymentModal(false);

    // Reset the Firestore trigger so it doesn't keep reopening
    try {
      const uiStateRef = doc(db, "ui_state", "payment_modal");
      await setDoc(uiStateRef, { show: false });
    } catch (error) {
      console.error("Error resetting payment modal trigger:", error);
    }
  };

  const addToCart = (product) => {
    if (product.inventoryEnabled && product.stockQuantity === 0) {
      return;
    }

    const existingItem = cart.find((item) => item.id === product.id);
    if (existingItem) {
      if (
        product.inventoryEnabled &&
        existingItem.quantity + 1 > product.stockQuantity
      ) {
        return;
      }
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity === 0) {
      removeFromCart(productId);
    } else {
      setCart(
        cart.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const filteredProducts = products.filter(
    (product) =>
      selectedCategory === "All" || product.category === selectedCategory
  );

  const generateOrderNumber = async () => {
    const year = new Date().getFullYear();
    const counterRef = doc(db, "counters", "orderCounter");

    try {
      const orderNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);

        let currentYear = year;
        let currentCount = 1;

        if (counterDoc.exists()) {
          const data = counterDoc.data();
          currentYear = data.year || year;
          currentCount = data.count || 0;

          if (currentYear !== year) {
            currentYear = year;
            currentCount = 1;
          } else {
            currentCount += 1;
          }
        }

        transaction.set(counterRef, {
          year: currentYear,
          count: currentCount,
        });

        const formattedNumber = String(currentCount).padStart(4, "0");
        return `${currentYear}/${formattedNumber}`;
      });

      return orderNumber;
    } catch (error) {
      console.error("Error generating order number:", error);
      throw error;
    }
  };

  const submitOrder = async () => {
    try {
      const sessionsCollection = collection(db, "sessions");
      const sessionsSnapshot = await getDocs(sessionsCollection);

      const today = new Date().toDateString();
      const activeSession = sessionsSnapshot.docs.find((doc) => {
        const session = doc.data();
        return (
          session.status === "active" &&
          new Date(session.startTime).toDateString() === today
        );
      });

      if (!activeSession) {
        setErrorMessage(
          "Cannot submit order: No active cashier session. Please contact staff to start a session."
        );
        setTimeout(() => {
          setErrorMessage("");
        }, 5000);
        return;
      }

      const orderNumber = await generateOrderNumber();

      const order = {
        orderNumber: orderNumber,
        items: cart,
        totalAmount: calculateTotal(),
        status: "pending",
        createdAt: serverTimestamp(),
        timestamp: new Date().toISOString(),
      };

      await addDoc(collection(db, "orders"), order);
      setOrderSubmitted(true);
      setCart([]);

      setTimeout(() => {
        setOrderSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error("Error submitting order:", error);
      setErrorMessage("Failed to submit order. Please try again.");
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mx-auto mb-4"></div>
          <p className="text-xl text-text-secondary">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 p-4 md:p-6">
      {/* Success Message */}
      {orderSubmitted && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="bg-success-50 border-l-4 border-success-500 rounded-lg shadow-large p-4 animate-bounce">
            <p className="text-success-800 font-semibold">
              ✓ Order submitted successfully! Waiting for cashier
              confirmation...
            </p>
          </div>
        </div>
      )}

      {/* Returned Order Message */}
      {returnedOrderMessage && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="bg-warning-50 border-l-4 border-warning-600 rounded-lg shadow-large p-4">
            <p className="text-warning-800 font-semibold">
              <span className="text-xl mr-2">⚠️</span>
              Order Returned: {returnedOrderMessage}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="bg-danger-50 border-l-4 border-danger-500 rounded-lg shadow-large p-4">
            <p className="text-danger-800 font-semibold">
              <span className="text-xl mr-2">🚫</span>
              Error: {errorMessage}
            </p>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        title="Payment Information"
        size="lg"
      >
        <div className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-xl p-6 -m-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
              <CreditCardIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-primary-900">
                Payment Information
              </h3>
              <p className="text-sm text-primary-700">
                Use these details to complete your payment
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {paymentSettings.accountNumber && (
              <div className="bg-white rounded-lg p-5 border-2 border-primary-200 shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <BanknotesIcon className="h-6 w-6 text-primary-600" />
                  <p className="text-xs font-bold text-primary-700 uppercase tracking-wide">
                    Account Number
                  </p>
                </div>
                <p className="text-2xl font-bold text-primary-900 tracking-wide">
                  {paymentSettings.accountNumber}
                </p>
              </div>
            )}

            {paymentSettings.viber && (
              <div className="bg-white rounded-lg p-5 border-2 border-success-200 shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                  <p className="text-xs font-bold text-success-700 uppercase tracking-wide">
                    Viber
                  </p>
                </div>
                <p className="text-2xl font-bold text-success-900 tracking-wide">
                  {paymentSettings.viber}
                </p>
              </div>
            )}

            {paymentSettings.qrCodeUrl && (
              <div className="bg-white rounded-lg p-5 border-2 border-primary-200 shadow-md">
                <div className="flex items-center gap-2 mb-3">
                  <QrCodeIcon className="h-6 w-6 text-primary-600" />
                  <p className="text-xs font-bold text-primary-700 uppercase tracking-wide">
                    Scan QR Code to Pay
                  </p>
                </div>
                <div className="flex justify-center bg-neutral-50 rounded-lg p-4">
                  <img
                    src={paymentSettings.qrCodeUrl}
                    alt="Payment QR Code"
                    className="max-w-sm w-full h-auto rounded-lg shadow-lg border-4 border-primary-300"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-info-100 border-2 border-info-300 rounded-lg">
            <p className="text-sm text-info-900 text-center font-medium">
              Please complete your payment using the information above and show
              the receipt to the cashier.
            </p>
          </div>
        </div>
      </Modal>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Category Filter */}
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`
                      px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all duration-200
                      ${
                        selectedCategory === category
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-white text-gray-800 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }
                    `}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filteredProducts.map((product) => {
                const isOutOfStock =
                  product.inventoryEnabled && product.stockQuantity === 0;
                const isLowStock =
                  product.inventoryEnabled &&
                  product.stockQuantity <= product.lowStockThreshold &&
                  product.stockQuantity > 0;

                return (
                  <div
                    key={product.id}
                    className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${
                      isOutOfStock ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                    onClick={() => !isOutOfStock && addToCart(product)}
                  >
                    {/* Product Image */}
                    <div className="relative aspect-square bg-gray-100 overflow-hidden">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <span className="text-gray-400 text-sm">No Image</span>
                        </div>
                      )}

                      {/* Stock Badge */}
                      {product.inventoryEnabled && (
                        <div className="absolute top-2 right-2">
                          {isOutOfStock ? (
                            <span className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
                              Out of Stock
                            </span>
                          ) : isLowStock ? (
                            <span className="px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full">
                              Low Stock
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                              In Stock
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-3 space-y-1">
                      <h3 className="font-bold text-sm text-gray-900 line-clamp-1">
                        {product.name}
                      </h3>

                      {product.category && (
                        <p className="text-xs text-gray-500 capitalize">
                          {product.category}
                        </p>
                      )}

                      <p className="text-base font-bold text-green-600 pt-0.5">
                        MVR {product.price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cart Section */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 max-h-[calc(100vh-7rem)] flex flex-col">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-neutral-200">
                <ShoppingCartIcon className="h-8 w-8 text-primary-500" />
                <h2 className="text-2xl font-bold text-text-primary">
                  Your Cart
                </h2>
              </div>

              {cart.length === 0 ? (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="text-center">
                    <ShoppingCartIcon className="h-20 w-20 text-neutral-300 mx-auto mb-4" />
                    <p className="text-text-muted text-lg">
                      Your cart is empty
                    </p>
                    <p className="text-text-muted text-sm mt-2">
                      Add some items to get started!
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-3 mb-6">
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        className="bg-neutral-50 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-text-primary">
                              {item.name}
                            </h4>
                            <p className="text-sm text-primary-600 font-medium">
                              MVR {item.price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 bg-white rounded-lg p-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(item.id, item.quantity - 1);
                              }}
                              className="h-8 w-8 flex items-center justify-center rounded-md bg-neutral-100 hover:bg-neutral-200 transition-colors"
                            >
                              <MinusIcon className="h-4 w-4" />
                            </button>
                            <span className="w-12 text-center font-semibold">
                              {item.quantity}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(item.id, item.quantity + 1);
                              }}
                              className="h-8 w-8 flex items-center justify-center rounded-md bg-neutral-100 hover:bg-neutral-200 transition-colors"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromCart(item.id);
                            }}
                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-neutral-200 pt-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-text-primary">
                        Total:
                      </span>
                      <span className="text-2xl font-bold text-primary-600">
                        MVR {calculateTotal().toFixed(2)}
                      </span>
                    </div>
                    <Button
                      variant="success"
                      size="lg"
                      fullWidth
                      onClick={submitOrder}
                    >
                      Submit Order
                    </Button>

                    {/* Payment Info Button */}
                    {paymentSettings.enabled && (
                      <Button
                        variant="outline"
                        size="md"
                        fullWidth
                        onClick={() => setShowPaymentModal(true)}
                        leftIcon={<CreditCardIcon className="h-5 w-5" />}
                      >
                        View Payment Info
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserOrderView;
